import { skipToken } from "@reduxjs/toolkit/query";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import type Guid from "_shared/guids/guid";
import { type PermissionKind, resourceFilterFor, useGetKindHolderCountsQuery } from "permissions/_api/permission-kinds-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { useGetPermissionsByGranteeQuery, useGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import Permission from "permissions/permission";
import { useMemo } from "react";

/** People per page of a kind's holders. */
export const KIND_HOLDERS_PAGE_SIZE = 25;

/** A person's grants of one standing kind are few; one page of the flat read holds a page of people's. */
const GRANTS_PAGE = PaginationParameters.create({ pageIndex: 0, pageSize: 100 });

/**
 * Who holds `kind`, counted: the grantee view filtered to the kind's containment, one row per
 * person. Its `totalElements` is the number of people, and each row's `totalPermissions` and
 * `permissionCounts` count only that kind's grants, because the server applies the filter before it
 * groups. Only live grants count; `showDisabled` is left at the server's default.
 */
export function kindHoldersQuery(kind: PermissionKind, pageIndex: number, pageSize: number): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Grantee,
    includePermissionPreviews: false,
    paginationParameters: PaginationParameters.create({ pageIndex, pageSize }),
    filterParameters: new PermissionFilterParameters({ resource: resourceFilterFor(kind) }),
  });
}

/** The live grants of `kind` held by `people`, read flat so each carries its parents for the Source cell. */
export function kindGrantsQuery(kind: PermissionKind, people: readonly Guid[]): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: GRANTS_PAGE,
    filterParameters: new PermissionFilterParameters({ resource: resourceFilterFor(kind), granteeUserIds: [...people] }),
  });
}

/**
 * How many people hold `kind`, for the home page's Kinds card and the header search's Kinds group.
 *
 * Every caller reads one answer, `GET /permissions/kinds/holders`, which counts every standing kind at
 * once: the home draws about 29 rows and sends one request for all of them, where it used to send one
 * each (#6312). Undefined until that answer arrives, and for a kind it does not count, such as a
 * per-record one. The kind page's own header counts from its page of holders instead.
 */
export function useKindHolderCount(kind: PermissionKind | undefined): number | undefined {
  const answer = useGetKindHolderCountsQuery(kind ? undefined : skipToken, {
    refetchOnFocus: true,
    selectFromResult: ({ data }) => ({ count: kind ? data?.[kind.slug] : undefined }),
  });
  return answer.count;
}

export type KindHolder = Readonly<{
  granteeUserId: Guid;
  /** How many live grants of the kind the person holds; for a per-record kind, how many records. */
  count: number;
}>;

export type KindHolders = Readonly<{
  /** The people on this page, most recently granted first, as the grantee view orders them. */
  holders: readonly KindHolder[];
  /** Every person who holds the kind, across all pages. */
  total?: number;
  /** For a standing kind, the live grants of the people on this page. */
  grants: readonly Permission[];
  isLoading: boolean;
  /** Any read of this page in flight, the first or a later one, as the shared table's states want it. */
  isFetching: boolean;
  error?: string;
  retry: () => void;
}>;

export default function useKindHolders(kind: PermissionKind, pageIndex: number): KindHolders {
  // Memoized on purpose: the queries are class instances that reach RTK Query's arguments.
  const holdersQuery = useMemo(() => kindHoldersQuery(kind, pageIndex, KIND_HOLDERS_PAGE_SIZE), [kind, pageIndex]);
  const holdersAnswer = useGetPermissionsByGranteeQuery({ query: holdersQuery }, { refetchOnFocus: true });

  const holders = useMemo(
    () =>
      PermissionsByGrantee.fromJSONArray(holdersAnswer.data?.data ?? []).map((row) => ({
        granteeUserId: row.granteeUserId,
        count: row.totalPermissions,
      })),
    [holdersAnswer.data],
  );

  const grantsQuery = useMemo(
    () => (kind.perRecord || holders.length === 0 ? undefined : kindGrantsQuery(kind, holders.map((holder) => holder.granteeUserId))),
    [kind, holders],
  );
  const grantsAnswer = useGetPermissionsQuery(grantsQuery ? { query: grantsQuery } : skipToken, { refetchOnFocus: true });
  const grants = useMemo(() => Permission.fromJSONArray(grantsAnswer.data?.data ?? []), [grantsAnswer.data]);

  const failure = holdersAnswer.error ?? grantsAnswer.error;
  return {
    holders,
    total: holdersAnswer.data?.headers.totalElements,
    grants,
    // Until the grants for this page of people have arrived the page is still loading: on the render
    // the holders arrive, the grants query has only just been given its arguments and does not yet
    // say it is loading, which would draw an empty table for a moment before the rows.
    isLoading: holdersAnswer.isLoading || (grantsQuery !== undefined && !grantsAnswer.data && !grantsAnswer.error),
    isFetching: holdersAnswer.isFetching || grantsAnswer.isFetching,
    error: failure ? (getErrorMessageFromUnknown(failure) ?? "The holders could not be loaded.") : undefined,
    retry: () => {
      void holdersAnswer.refetch();
      if (grantsQuery) void grantsAnswer.refetch();
    },
  };
}
